import { useState } from 'react'
import type { Deposant, GroupeCommission } from '@troc/shared'
import {
  GROUPE_COMMISSION_DEFAUT,
  GROUPES_COMMISSION,
  LIBELLE_GROUPE_COMMISSION,
  TITRES,
} from '@troc/shared'

/**
 * Fiche client — reprend les champs de la « Signalétique client » de l'ancien
 * logiciel. La date de naissance et le numéro de carte d'identité ne sont pas
 * du confort administratif : la revente d'objets d'occasion impose d'identifier
 * le déposant. Ils restent facultatifs à la saisie pour ne pas bloquer un
 * dépôt en cours, mais l'écran le signale.
 */

export interface SaisieClient {
  nom: string
  titre: string
  tel: string
  gsm: string
  email: string
  adresse: string
  codePostal: string
  ville: string
  pays: string
  dateNaissance: string
  numIdentite: string
  iban: string
  notes: string
  identiteValidee: string
  groupeCommission: string
}

export const CLIENT_VIDE: SaisieClient = {
  nom: '',
  titre: '',
  tel: '',
  gsm: '',
  email: '',
  adresse: '',
  codePostal: '',
  ville: '',
  pays: 'B',
  dateNaissance: '',
  numIdentite: '',
  iban: '',
  notes: '',
  identiteValidee: 'false',
  groupeCommission: GROUPE_COMMISSION_DEFAUT,
}

export function depuisDeposant(deposant: Deposant): SaisieClient {
  return {
    nom: deposant.nom,
    titre: deposant.titre,
    tel: deposant.tel,
    gsm: deposant.gsm,
    email: deposant.email,
    adresse: deposant.adresse,
    codePostal: deposant.codePostal,
    ville: deposant.ville,
    pays: deposant.pays || 'B',
    dateNaissance: deposant.dateNaissance,
    numIdentite: deposant.numIdentite,
    iban: deposant.iban,
    notes: deposant.notes,
    identiteValidee: String(deposant.identiteValidee),
    groupeCommission: deposant.groupeCommission,
  }
}

export function FormulaireClient({
  valeur,
  onChange,
}: {
  valeur: SaisieClient
  onChange: (valeur: SaisieClient) => void
}) {
  const [notesOuvertes, setNotesOuvertes] = useState(valeur.notes !== '')
  const modifier = (champ: keyof SaisieClient) => (nouvelle: string) =>
    onChange({ ...valeur, [champ]: nouvelle })

  const identiteIncomplete =
    (!valeur.dateNaissance || !valeur.numIdentite) && valeur.identiteValidee !== 'true'

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 780 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
        <Champ id="titre" libelle="Titre">
          <select
            id="titre"
            className="saisie saisie-grande"
            value={valeur.titre}
            onChange={(e) => modifier('titre')(e.target.value)}
          >
            {TITRES.map((titre) => (
              <option key={titre || 'aucun'} value={titre}>
                {titre || '—'}
              </option>
            ))}
          </select>
        </Champ>
        <Champ id="nom" libelle="Nom et prénom">
          <input
            id="nom"
            className="saisie saisie-grande"
            value={valeur.nom}
            onChange={(e) => modifier('nom')(e.target.value)}
            autoFocus
            autoCapitalize="characters"
          />
        </Champ>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Champ id="gsm" libelle="GSM">
          <input
            id="gsm"
            className="saisie saisie-grande"
            inputMode="tel"
            placeholder="0475 12 34 56"
            value={valeur.gsm}
            onChange={(e) => modifier('gsm')(e.target.value)}
          />
        </Champ>
        <Champ id="tel" libelle="Téléphone fixe">
          <input
            id="tel"
            className="saisie saisie-grande"
            inputMode="tel"
            placeholder="063 00 00 00"
            value={valeur.tel}
            onChange={(e) => modifier('tel')(e.target.value)}
          />
        </Champ>
      </div>

      <Champ id="email" libelle="E-mail">
        <input
          id="email"
          className="saisie saisie-grande"
          inputMode="email"
          autoCapitalize="none"
          value={valeur.email}
          onChange={(e) => modifier('email')(e.target.value)}
        />
      </Champ>

      <Champ id="adresse" libelle="Adresse — rue et numéro">
        <input
          id="adresse"
          className="saisie saisie-grande"
          placeholder="Rue du Vallon 50"
          value={valeur.adresse}
          onChange={(e) => modifier('adresse')(e.target.value)}
        />
      </Champ>

      <div style={{ display: 'grid', gridTemplateColumns: '90px 140px 1fr', gap: 12 }}>
        <Champ id="pays" libelle="Pays">
          <input
            id="pays"
            className="saisie saisie-grande"
            value={valeur.pays}
            onChange={(e) => modifier('pays')(e.target.value.toUpperCase())}
          />
        </Champ>
        <Champ id="cp" libelle="Code postal">
          <input
            id="cp"
            className="saisie saisie-grande"
            inputMode="numeric"
            placeholder="6780"
            value={valeur.codePostal}
            onChange={(e) => modifier('codePostal')(e.target.value)}
          />
        </Champ>
        <Champ id="ville" libelle="Localité">
          <input
            id="ville"
            className="saisie saisie-grande"
            placeholder="MESSANCY"
            autoCapitalize="characters"
            value={valeur.ville}
            onChange={(e) => modifier('ville')(e.target.value)}
          />
        </Champ>
      </div>

      <hr className="filet" style={{ margin: 0 }} />

      <div className="etiquette">Identité — registre des objets d’occasion</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Champ id="naissance" libelle="Date de naissance">
          <input
            id="naissance"
            type="date"
            className="saisie saisie-grande"
            value={valeur.dateNaissance}
            onChange={(e) => modifier('dateNaissance')(e.target.value)}
          />
        </Champ>
        <Champ id="ci" libelle="N° de carte d’identité">
          <input
            id="ci"
            className="saisie saisie-grande"
            placeholder="591-2847361-22"
            value={valeur.numIdentite}
            onChange={(e) => modifier('numIdentite')(e.target.value)}
          />
        </Champ>
      </div>
      {identiteIncomplete && (
        <div className="note">
          Il manque la date de naissance ou le numéro de carte d’identité.
          <br />
          <button
            type="button"
            className="btn btn-fantome"
            style={{ paddingLeft: 0 }}
            onClick={() => modifier('identiteValidee')('true')}
          >
            Le client ne souhaite pas les communiquer — valider la fiche telle quelle
          </button>
        </div>
      )}
      {valeur.identiteValidee === 'true' && (
        <div className="note" style={{ borderColor: 'var(--color-succes-700)' }}>
          Fiche validée par un vendeur malgré des informations manquantes.{' '}
          <button
            type="button"
            className="btn btn-fantome"
            style={{ paddingLeft: 0 }}
            onClick={() => modifier('identiteValidee')('false')}
          >
            Annuler cette validation
          </button>
        </div>
      )}

      <hr className="filet" style={{ margin: 0 }} />

      <div className="etiquette">Commission</div>
      <Champ id="groupe-commission" libelle="Groupe de commission">
        <select
          id="groupe-commission"
          className="saisie saisie-grande"
          style={{ maxWidth: 220 }}
          value={valeur.groupeCommission || GROUPE_COMMISSION_DEFAUT}
          onChange={(e) => modifier('groupeCommission')(e.target.value)}
        >
          {GROUPES_COMMISSION.map((groupe) => (
            <option key={groupe} value={groupe}>
              Groupe {groupe}
              {groupe === GROUPE_COMMISSION_DEFAUT ? ' (général)' : ''}
            </option>
          ))}
        </select>
      </Champ>
      <div className="note" style={{ fontSize: 12, lineHeight: 1.6 }}>
        <strong>Légende des groupes</strong>
        <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {(Object.entries(LIBELLE_GROUPE_COMMISSION) as [GroupeCommission, string][]).map(
            ([groupe, libelle]) => (
              <li key={groupe}>
                <strong>{groupe}</strong> — {libelle}
              </li>
            ),
          )}
        </ul>
      </div>

      <Champ id="iban" libelle="IBAN — pour les reversements par virement">
        <input
          id="iban"
          className="saisie saisie-grande"
          placeholder="BE68 5390 0754 7034"
          autoCapitalize="characters"
          value={valeur.iban}
          onChange={(e) => modifier('iban')(e.target.value.toUpperCase())}
        />
      </Champ>

      {notesOuvertes ? (
        <Champ id="notes" libelle="Notes">
          <textarea
            id="notes"
            className="saisie"
            style={{ minHeight: 90, resize: 'vertical' }}
            value={valeur.notes}
            onChange={(e) => modifier('notes')(e.target.value)}
          />
        </Champ>
      ) : (
        <button
          type="button"
          className="btn btn-fantome"
          style={{ alignSelf: 'flex-start' }}
          onClick={() => setNotesOuvertes(true)}
        >
          + Ajouter une note
        </button>
      )}
    </div>
  )
}

function Champ({
  id,
  libelle,
  children,
}: {
  id: string
  libelle: string
  children: React.ReactNode
}) {
  return (
    <div className="champ">
      <label htmlFor={id}>{libelle}</label>
      {children}
    </div>
  )
}
